import {useEffect, useRef, useState, type RefObject} from 'react';
import {ContextualSaveBar} from '@shopify/polaris';

/**
 * Production wiring for one page's editable forms onto Polaris's single
 * ContextualSaveBar.
 *
 * The model, given the native-POST (post-redirect-get) forms used across slj:
 *
 * - Each covered form registers its fields with the values the page loaded with
 *   (snapshotted on first render — a save is a full navigation, so "initial"
 *   never changes underneath us).
 * - Editing a field marks its form dirty. The MOST RECENTLY EDITED dirty form
 *   owns the bar; an idle dirty form stays dirty underneath and resurfaces when
 *   the owner is saved (navigation) or discarded.
 * - Save calls form.requestSubmit() — the existing native POST is unchanged,
 *   and requestSubmit (unlike .submit()) runs native constraint validation, so
 *   type="email" / minLength are enforced and focus the offender.
 * - Discard resets that form to its initial snapshot.
 * - While anything is dirty, a beforeunload guard catches nav-away/close —
 *   client-only, nothing renders differently server-side. The guard stands
 *   down for the save bar's OWN Save: save() marks the form via a one-time
 *   `submit` listener (which fires only after constraint validation passes),
 *   otherwise every intended save would pop a spurious "Leave site?" prompt.
 *
 * Fresh action objects each render are safe here: ContextualSaveBar's
 * re-registration setState re-renders Frame with identical children, so the
 * cycle terminates after one extra reconcile per keystroke.
 */

export interface DirtyFormHandle<V extends Record<string, unknown>> {
  values: V;
  dirty: boolean;
  formRef: RefObject<HTMLFormElement>;
  set<F extends keyof V>(field: F, value: V[F]): void;
  reset(): void;
  save(): void;
}

function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return aKeys.length === bKeys.length && aKeys.every((k) => Object.is(a[k], b[k]));
}

export function useUnsavedChanges<T extends Record<string, Record<string, unknown>>>(
  initial: T,
  message = 'Unsaved changes',
) {
  const [initialValues] = useState(initial);
  const [values, setValues] = useState<T>(initial);
  const seq = useRef(0);
  const touchedAt = useRef<Record<string, number>>({});
  const formRefs = useRef<Record<string, RefObject<HTMLFormElement>>>({});
  // Set (by a one-time `submit` listener in save()) only when a save-bar Save
  // actually submits — i.e. after constraint validation passed. Cleared by any
  // further edit or discard, so a failed/aborted save re-arms the guard.
  const submittedRef = useRef(false);

  const forms = {} as {[K in keyof T]: DirtyFormHandle<T[K]>};
  for (const key of Object.keys(initialValues) as (keyof T)[]) {
    const k = key as string;
    const formRef = (formRefs.current[k] ??= {current: null});
    forms[key] = {
      values: values[key],
      dirty: !shallowEqual(values[key] ?? {}, initialValues[key] ?? {}),
      formRef,
      set: (field, value) => {
        submittedRef.current = false;
        touchedAt.current[k] = ++seq.current;
        setValues((v) => ({...v, [key]: {...v[key], [field]: value}}));
      },
      reset: () => {
        submittedRef.current = false;
        setValues((v) => ({...v, [key]: initialValues[key]}));
      },
      save: () => {
        const el = formRef.current;
        if (!el) return;
        el.addEventListener('submit', () => { submittedRef.current = true; }, {once: true});
        el.requestSubmit();
      },
    };
  }

  // The bar belongs to the most recently touched form that is still dirty.
  let active: DirtyFormHandle<Record<string, unknown>> | null = null;
  let best = 0;
  for (const k of Object.keys(initialValues)) {
    const handle = forms[k as keyof T] as DirtyFormHandle<Record<string, unknown>>;
    const touched = touchedAt.current[k] ?? 0;
    if (handle.dirty && touched >= best) {
      best = touched;
      active = handle;
    }
  }

  const anyDirty = active !== null;

  useEffect(() => {
    if (!anyDirty) return;
    const guard = (event: BeforeUnloadEvent) => {
      // A save-bar Save is an intended navigation — don't prompt on it.
      if (submittedRef.current) return;
      event.preventDefault();
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [anyDirty]);

  const saveBar = active ? (
    <ContextualSaveBar
      message={message}
      saveAction={{onAction: active.save}}
      discardAction={{onAction: active.reset}}
    />
  ) : null;

  return {forms, anyDirty, saveBar};
}
